import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormField,
  Modal,
  Multiselect,
  SpaceBetween,
  StatusIndicator,
  Textarea,
  TokenGroup,
} from '@amzn/awsui-components-react';
import { createIndustryGrants } from '../../client/industryAdminClient';
import { denialHint } from '../../utils/authorization';
import {
  MAX_ALIASES_PER_CALL,
  grantConcurrently,
  parseAliases,
  type GrantOutcome,
} from './industryAccess';

const describeError = (e: unknown) =>
  denialHint(e, 'en') ?? (e as Error | null)?.message ?? 'Could not grant access.';

/**
 * Grant a pasted list of aliases to one or more industries — the row's "+ Add" opens it with
 * that industry preselected, the tab's "Grant access" with none.
 *
 * One CreateIndustryGrants call per industry, all at once; see grantConcurrently for how
 * unknown aliases and other failures are told apart.
 */
export function GrantAccessModal({
  industries,
  initialIndustries,
  onDismiss,
  onChanged,
}: {
  industries: readonly string[];
  initialIndustries: readonly string[];
  onDismiss: () => void;
  /** After every run, so the table reflects what was written even when some failed. */
  onChanged: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([...initialIndustries]);
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const [unknownAliases, setUnknownAliases] = useState<string | null>(null);
  // The last run's aliases travel with its outcomes, so "Retry failed" repeats that run
  // rather than whatever the inputs say now. Editing the inputs drops it.
  const [lastRun, setLastRun] = useState<{ aliases: string[]; outcomes: Map<string, GrantOutcome> } | null>(
    null,
  );
  const outcomes = lastRun?.outcomes;

  const aliases = useMemo(() => parseAliases(text), [text]);
  const tooMany = aliases.length > MAX_ALIASES_PER_CALL;
  const failed = [...(outcomes ?? [])].filter(([, o]) => !o.ok).map(([code]) => code);
  const succeeded = [...(outcomes ?? [])].filter(([, o]) => o.ok).map(([code]) => code);
  const canSubmit = selected.length > 0 && aliases.length > 0 && !tooMany && !pending;

  const options = industries.map((code) => ({ value: code, label: code }));

  const inputsChanged = () => {
    setLastRun(null);
    setUnknownAliases(null);
  };

  const run = async (codes: readonly string[], runAliases: string[], merge: boolean) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setUnknownAliases(null);
    try {
      const result = await grantConcurrently(codes, runAliases, createIndustryGrants);
      const next = new Map(merge ? outcomes ?? [] : []);
      result.outcomes.forEach((o, code) => next.set(code, o));
      const unknown = result.unknownAliases.length > 0;
      if (unknown) setUnknownAliases(result.unknownAliases.join(' '));
      if (result.outcomes.size > 0) onChanged();
      if (!unknown && [...next.values()].every((o) => o.ok)) {
        onDismiss();
        return;
      }
      setLastRun(next.size > 0 ? { aliases: runAliases, outcomes: next } : null);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  return (
    <Modal
      visible
      // Not dismissable mid-request: the outcome would have nowhere to be shown.
      onDismiss={() => {
        if (!pending) onDismiss();
      }}
      header={initialIndustries.length === 1 ? `Add people to ${initialIndustries[0]}` : 'Grant access'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={pending}>
              Cancel
            </Button>
            {failed.length > 0 && (
              <Button disabled={pending} onClick={() => lastRun && void run(failed, lastRun.aliases, true)}>
                Retry failed
              </Button>
            )}
            <Button
              variant="primary"
              disabled={!canSubmit}
              loading={pending}
              onClick={() => void run(selected, aliases, false)}
            >
              Add
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label="Industries">
          <Multiselect
            selectedOptions={options.filter((o) => selected.includes(o.value))}
            onChange={({ detail }) => {
              setSelected(detail.selectedOptions.map((o) => o.value ?? '').filter(Boolean));
              inputsChanged();
            }}
            options={options}
            filteringType="auto"
            placeholder="Choose industries"
            empty="No industries"
            disabled={pending}
          />
        </FormField>
        <FormField
          label="Aliases"
          description="Paste aliases separated by commas, spaces or new lines."
          constraintText={`${aliases.length} ${aliases.length === 1 ? 'alias' : 'aliases'}`}
          errorText={tooMany ? `At most ${MAX_ALIASES_PER_CALL} aliases at a time.` : undefined}
        >
          <Textarea
            value={text}
            onChange={({ detail }) => {
              setText(detail.value);
              inputsChanged();
            }}
            rows={6}
            placeholder="alias1, alias2"
            ariaLabel="Aliases"
            disabled={pending}
          />
        </FormField>
        {aliases.length > 0 && <TokenGroup readOnly limit={30} items={aliases.map((a) => ({ label: a }))} />}
        {unknownAliases && (
          <Alert type="error" header={succeeded.length > 0 ? 'Partially granted' : 'Nothing was granted'}>
            {unknownAliases}
            {succeeded.length > 0 && ` Granted to: ${succeeded.join(', ')}.`}
          </Alert>
        )}
        {outcomes && !unknownAliases && (
          <SpaceBetween size="xxs">
            {[...outcomes].map(([code, o]) => (
              <StatusIndicator key={code} type={o.ok ? 'success' : 'error'}>
                {o.ok ? code : `${code}: ${describeError(o.error)}`}
              </StatusIndicator>
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
}
